import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { apiFetch, ApiCallError } from '../lib/api.ts';
import type { ReceiptSummary } from './ReceivingListPage.tsx';

interface CartonGroup {
  pcsPerCarton: number;
  cartonTags: number;
}
interface Draft {
  sku: string;
  qtyExpected: number;
  unitTags: number;
  cartons: CartonGroup[];
  isExcluded: boolean;
  declaredQty: number | null;
}
interface SubmitResult {
  receiptNo: string;
  linesWritten: number;
  planQty: number;
  subReceiptNo: string | null;
}

/**
 * Pieces this line accounts for. An untagged line has no tags to count, so it
 * is whatever the operator recorded — falling back to the ordered quantity,
 * which is the "assumed correct" rule receipts have always used.
 */
const piecesOf = (l: Draft): number =>
  l.isExcluded
    ? (l.declaredQty ?? l.qtyExpected)
    : l.unitTags + l.cartons.reduce((t, c) => t + c.pcsPerCarton * c.cartonTags, 0);

export function ReceiptKeyingPage() {
  const { receiptNo = '' } = useParams();
  const qc = useQueryClient();
  const path = `/api/dashboard/receipts/${encodeURIComponent(receiptNo)}`;

  const q = useQuery({
    queryKey: ['receipt', receiptNo],
    queryFn: () => apiFetch<ReceiptSummary>(path),
  });

  const [lines, setLines] = useState<Draft[]>([]);
  const [note, setNote] = useState('');
  const [result, setResult] = useState<SubmitResult | null>(null);

  const fromServer = useMemo<Draft[]>(
    () =>
      (q.data?.lines ?? []).map((l) => ({
        sku: l.sku,
        qtyExpected: l.qtyExpected,
        unitTags: l.unitTags,
        cartons: l.cartons.map((c) => ({ ...c })),
        isExcluded: Boolean(l.isExcluded),
        declaredQty: l.declaredQty ?? null,
      })),
    [q.data],
  );

  // Seed the editable copy whenever the server's version changes. qtyExpected
  // and the RFID classification stay server-owned and are never sent back.
  useEffect(() => {
    setLines(fromServer);
    setNote(q.data?.overKeyedNote ?? '');
  }, [fromServer, q.data]);

  // Nothing to save until something actually differs from what the server holds.
  const dirty = useMemo(
    () =>
      JSON.stringify(lines) !== JSON.stringify(fromServer) ||
      note.trim() !== (q.data?.overKeyedNote ?? ''),
    [lines, fromServer, note, q.data],
  );

  const keyingBody = () => ({
    lines: lines.map((l) => ({
      sku: l.sku,
      unitTags: l.unitTags,
      cartons: l.cartons,
      declaredQty: l.declaredQty,
    })),
    note: note.trim() ? note.trim() : null,
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['receipt', receiptNo] });
    void qc.invalidateQueries({ queryKey: ['receipts'] });
  };

  /** One save for the whole receipt — tag split and recorded quantities alike. */
  const save = useMutation({
    mutationFn: () =>
      apiFetch(`${path}/keying`, { method: 'PUT', body: JSON.stringify(keyingBody()) }),
    onSuccess: refresh,
  });

  const submit = useMutation({
    mutationFn: async () => {
      await apiFetch(`${path}/keying`, { method: 'PUT', body: JSON.stringify(keyingBody()) });
      return apiFetch<SubmitResult>(`${path}/submit`, {
        method: 'POST',
        body: JSON.stringify({ note: note.trim() ? note.trim() : null }),
      });
    },
    onSuccess: (r) => {
      setResult(r);
      refresh();
    },
  });

  const reopen = useMutation({
    mutationFn: () => apiFetch(`${path}/reopen`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const cancel = useMutation({
    mutationFn: () => apiFetch(`${path}/cancel`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const patch = (i: number, next: Partial<Draft>): void =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...next } : l)));

  if (q.isLoading) return <div className="loading">Loading…</div>;
  if (q.error) return <div className="error">{(q.error as Error).message}</div>;
  if (!q.data) return null;

  const receipt = q.data;
  const isDraft = receipt.status === 'draft';
  // Submitted means settled — for tags and recorded quantities alike. The
  // SmartLink order is out, and any figure here may already have been carried
  // to a sub PO, where changing it would credit OBM twice. Correcting a
  // submitted receipt goes through re-open.
  const recordable = isDraft;
  const totalExpected = lines.reduce((t, l) => t + l.qtyExpected, 0);
  const totalKeyed = lines.reduce((t, l) => t + piecesOf(l), 0);
  const overKeyed = lines.some((l) => piecesOf(l) > l.qtyExpected);
  const anyUntagged = lines.some((l) => l.isExcluded);
  const busy = save.isPending || submit.isPending;
  const err = (submit.error ?? save.error ?? cancel.error ?? reopen.error) as
    | ApiCallError
    | Error
    | null;

  return (
    <>
      <Link to="/receiving" className="back-link">
        ← Back to Receiving
      </Link>
      <header className="main-header">
        <h1 className="main-title">
          {receipt.seq > 1 ? 'Sub PO ' : 'Receipt '}
          <span className="mono">{receipt.receiptNo}</span>
        </h1>
        <span className="main-subtitle">
          against{' '}
          <Link to={`/purchase-orders/${encodeURIComponent(receipt.poNo)}`}>{receipt.poNo}</Link>
          {' · '}
          <span className={`badge ${isDraft ? 'badge-pending' : 'badge-ok'}`}>
            {receipt.status}
          </span>
        </span>
      </header>

      {result && (
        <div className="notice notice-ok">
          Submitted — {result.linesWritten} lines / {result.planQty} tags pushed to SmartLink.{' '}
          {result.subReceiptNo ? (
            <>
              Balance moved to sub PO{' '}
              <Link to={`/receiving/${encodeURIComponent(result.subReceiptNo)}`}>
                {result.subReceiptNo}
              </Link>
              .
            </>
          ) : (
            'Nothing outstanding — no sub PO created.'
          )}
        </div>
      )}
      {err && <div className="error">{err.message}</div>}

      <table className="table">
        <thead>
          <tr>
            <th>SKU</th>
            <th className="num">Expected</th>
            <th className="num">Individual tags</th>
            <th>Cartons (pcs × tags)</th>
            <th className="num">Received</th>
            <th className="num">Balance</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => {
            const keyed = piecesOf(l);
            return (
              <tr key={l.sku}>
                <td className="mono">
                  {l.sku}
                  {l.isExcluded && (
                    <div className="keying-summary">not RFID-tracked — recorded, never scanned</div>
                  )}
                </td>
                <td className="num">{l.qtyExpected}</td>
                <td className="num">
                  {l.isExcluded ? (
                    // No tags exist for this SKU, so the operator records pieces
                    // directly instead of a tag count.
                    <input
                      type="number"
                      min={0}
                      value={l.declaredQty ?? l.qtyExpected}
                      disabled={!recordable}
                      onChange={(e) =>
                        patch(i, { declaredQty: Math.max(0, Number(e.target.value) || 0) })
                      }
                    />
                  ) : (
                    <input
                      type="number"
                      min={0}
                      value={l.unitTags}
                      disabled={!isDraft}
                      onChange={(e) =>
                        patch(i, { unitTags: Math.max(0, Number(e.target.value) || 0) })
                      }
                    />
                  )}
                </td>
                <td>
                  {l.isExcluded ? (
                    <span className="keying-summary">—</span>
                  ) : (
                    <>
                      {l.cartons.map((c, ci) => (
                        <div key={ci} className="carton-row">
                          <input
                            type="number"
                            min={1}
                            value={c.pcsPerCarton}
                            disabled={!isDraft}
                            onChange={(e) =>
                              patch(i, {
                                cartons: l.cartons.map((x, j) =>
                                  j === ci ? { ...x, pcsPerCarton: Number(e.target.value) || 0 } : x,
                                ),
                              })
                            }
                          />
                          {' × '}
                          <input
                            type="number"
                            min={1}
                            value={c.cartonTags}
                            disabled={!isDraft}
                            onChange={(e) =>
                              patch(i, {
                                cartons: l.cartons.map((x, j) =>
                                  j === ci ? { ...x, cartonTags: Number(e.target.value) || 0 } : x,
                                ),
                              })
                            }
                          />
                          {' = '}
                          {c.pcsPerCarton * c.cartonTags} pcs
                          {isDraft && (
                            <button
                              type="button"
                              onClick={() =>
                                patch(i, { cartons: l.cartons.filter((_, j) => j !== ci) })
                              }
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      {isDraft && (
                        <button
                          type="button"
                          onClick={() =>
                            patch(i, {
                              cartons: [...l.cartons, { pcsPerCarton: 1, cartonTags: 1 }],
                            })
                          }
                        >
                          Add carton size
                        </button>
                      )}
                    </>
                  )}
                </td>
                <td className="num">{keyed}</td>
                <td className="num">{Math.max(0, l.qtyExpected - keyed)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="keying-summary">
        {totalKeyed} of {totalExpected} pcs accounted for
        {totalKeyed < totalExpected && ` — ${totalExpected - totalKeyed} pcs carry to a sub PO`}.
        {anyUntagged &&
          ' Untagged lines are never sent to SmartLink; what you record reaches OBM as entered.'}
      </p>

      {overKeyed &&
        (isDraft ? (
          <div className="notice notice-warn">
            <label>
              More received than ordered — a reason is required:{' '}
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} />
            </label>
          </div>
        ) : (
          <div className="notice notice-warn">
            More received than ordered — recorded reason:{' '}
            {receipt.overKeyedNote ? <em>{receipt.overKeyedNote}</em> : <em>none given</em>}
          </div>
        ))}

      <div className="toolbar">
        {recordable && (
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={busy || !dirty}
            title={dirty ? undefined : 'Nothing has changed since the last save'}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
        {isDraft && (
          <>
            <button
              type="button"
              className="primary"
              onClick={() => submit.mutate()}
              disabled={busy || totalKeyed === 0 || (overKeyed && !note.trim())}
            >
              Submit &amp; push to SmartLink
            </button>
            <button
              type="button"
              className="danger"
              onClick={() => cancel.mutate()}
              disabled={busy || cancel.isPending}
            >
              {receipt.seq > 1 ? 'Kill off this sub PO' : 'Cancel this receipt'}
            </button>
          </>
        )}
      </div>

      {receipt.status === 'submitted' &&
        (receipt.hasChild ? (
          // Re-open is refused once a sub PO holds the balance — offering the
          // button anyway would just produce an error on click.
          <p className="keying-summary">
            The tag split is frozen: sub PO {`${receipt.poNo}-${receipt.seq}`} already holds
            the outstanding balance, so re-opening this receipt would let the same pieces be keyed
            twice. Key the sub PO instead.
          </p>
        ) : (
          <div className="toolbar">
            <button type="button" onClick={() => reopen.mutate()} disabled={reopen.isPending}>
              Re-open for keying
            </button>
            <span className="keying-summary">
              Pulls this receipt back to draft so cartons can be keyed. Cancels the SmartLink
              order first, and is refused once anything has been scanned.
            </span>
          </div>
        ))}
    </>
  );
}
